import 'react-dates/initialize';
import React, { memo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { DateRangePicker, SingleDatePicker } from 'react-dates';
import uniqId from 'uniqid';

import Icon from '../Icon';
import Label from '../Label';

import styles from './Date2.module.scss';

const addCustomClass = () => {
  const element = document.querySelector('.DateRangePicker_picker_1');
  element && element.classList.add('kolkit_dateRangePicker');
}

const Date2 = props => {
  const {
    id, date, appendToBody, className, onDatesChange, onDateChange, singleDate, dateLabel, dateLabelIcon,
    startDate, endDate, startDateLabel, endDateLabel, startDateLabelIcon, endDateLabelIcon, isOutsideRange, renderMonthElement,
    disabled, size, displayFormat, startDatePlaceholder, endDatePlaceholder, startDateId, endDateId, required, startRequired, endRequired,
    ...rest
  } = props;
  const [focusedInput, setFocusedInput] = useState(null);
  const [focused, setFocused] = useState(false);
  const [isDate2] = useState(true);

  useEffect(() => {
    if (focusedInput && isDate2) addCustomClass();
  }, [focusedInput, isDate2])

  const handleFocusChange = useCallback(
    focusedInput => setFocusedInput(focusedInput),
    []
  );

  const handleSingleFocusChange = useCallback(
    ({ focused }) => setFocused(focused),
    []
  );

  const cnDate = cn(styles.date2, styles[size], className, {
    [styles.singleDate]: singleDate
  });

  return singleDate ? (
    <div className={cnDate} {...rest}>
      <Label className={styles.label} label={dateLabel} icon={dateLabelIcon} required={required} />
      <SingleDatePicker
        {...props}
        id={id}
        date={date}
        onDateChange={onDateChange}
        focused={focused}
        onFocusChange={handleSingleFocusChange}
        numberOfMonths={1}
        //renderMonthElement={handleRenderMonthElement}
        displayFormat={displayFormat}
        navPrev={
          <div className={styles.left}>
            <Icon label="arrow-left" theme="regular" isButton />
          </div>
        }
        navNext={
          <div className={styles.right}>
            <Icon label="arrow-right" theme="regular" isButton />
          </div>
        }
      />
    </div>
  ) : (
    <div className={cnDate} {...rest}>
      <div className={styles.labels}>
        <Label className={styles.label} label={startDateLabel} icon={startDateLabelIcon} required={startRequired} />
        <Label className={styles.label} label={endDateLabel} icon={endDateLabelIcon} required={endRequired} />
      </div>
      <DateRangePicker
        {...props}
        startDateId={startDateId}
        endDateId={endDateId}
        startDate={startDate}
        endDate={endDate}
        onDatesChange={onDatesChange}
        onFocusChange={handleFocusChange}
        focusedInput={focusedInput}
        disabled={disabled}
        firstDayOfWeek={1}
        isOutsideRange={isOutsideRange}
        startDatePlaceholderText={startDatePlaceholder}
        endDatePlaceholderText={endDatePlaceholder}
        displayFormat={displayFormat}
        hideKeyboardShortcutsPanel
        customArrowIcon={<></>}
        navPrev={
          <div className={styles.left}>
            <Icon label="arrow-left" theme="regular" isButton />
          </div>
        }
        navNext={
          <div className={styles.right}>
            <Icon label="arrow-right" theme="regular" isButton />
          </div>
        }
        appendToBody={appendToBody}
      />
    </div>
  )
};

Date2.displayName = 'Date2';

Date2.propTypes = {
  appendToBody: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  date: PropTypes.string,
  dateLabel: PropTypes.string,
  dateLabelIcon: PropTypes.string,
  startDate: PropTypes.any.isRequired,
  endDate: PropTypes.any.isRequired,
  startDateLabel: PropTypes.string,
  endDateLabel: PropTypes.string,
  startDateLabelIcon: PropTypes.string,
  endDateLabelIcon: PropTypes.string,
  onDatesChange: PropTypes.func,
  onDateChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  startRequired: PropTypes.bool,
  endRequired: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  displayFormat: PropTypes.any,
  startDatePlaceholder: PropTypes.string,
  endDatePlaceholder: PropTypes.string,
  startDateId: PropTypes.string,
  endDateId: PropTypes.string,
  singleDate: PropTypes.bool,
  isOutsideRange: PropTypes.func,
  renderMonthElement: PropTypes.func
};

Date2.defaultProps = {
  id: uniqId(),
  appendToBody: false,
  className: null,
  date: null,
  dateLabel: null,
  dateLabelIcon: null,
  onDatesChange: () => {},
  onDateChange: () => {},
  startDateLabel: null,
  endDateLabel: null,
  startDateLabelIcon: null,
  endDateLabelIcon: null,
  disabled: false,
  required: false,
  startRequired: false,
  endRequired: false,
  size: 'medium',
  displayFormat: 'DD/MM/YYYY',
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  startDateId: uniqId(),
  endDateId: uniqId(),
  singleDate: false,
  isOutsideRange: () => false,
  renderMonthElement: null
}

export default memo(Date2);
